import { BundledProduct, NatureOfDamage, OriginalRegDocument, ProductTypeEN, YesNoSelections } from 'cpfr-common';
import { ValidatedCheckbox, ValidatedSelect, ValidatedSelectItem } from '@cp-shared-19/frontend-ui';
import { Layout } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DateInput,
    EmailInput,
    FileUpload,
    NatureOfDamageSelection,
    OriginalRegDocumentSelection,
    PhonenumberInput,
    PostcodeInput,
    TextArea,
    TextInput,
} from '../../simple-form-components';
import { ReparableSelection } from '../../simple-form-components/reparable/ReparableSelection';
import { CreditUpload } from './CreditUpload';
import { CircumstancesOfDisasterSelection } from '../../simple-form-components/circumstances-of-disaster-selection/CircumstancesOfDisasterSelection';
import { CauseOfSinistreSelection } from '../../simple-form-components/cause-of-sinistre-selection/CauseOfSinistreSelection';

type VehicleClaimFormProps = {
    productTypeEn?: ProductTypeEN;
    productType?: string;
    bundledProducts?: BundledProduct[];
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const VehicleClaimForm: React.FC<VehicleClaimFormProps> = ({ productTypeEn, bundledProducts }) => {
    const { t } = useTranslation('request');
    const [field] = useField('vehicleClaim.nonReparableFields.originalRegDocument');
    const [fieldReparable] = useField('vehicleClaim.reparable');
    const [fieldExpertReportAvailable] = useField('vehicleClaim.nonReparableFields.expertReportAvailable');
    const [fieldNatureOfDamage] = useField<NatureOfDamage>('vehicleClaim.nonReparableFields.natureOfDamage');
    const selectedOriginalRegDocument: OriginalRegDocument = field.value;
    const translationPrefix = 'category-selection.vehicle-claim';

    const translate = (key: string): string => {
        return t(`${translationPrefix}.${key}`);
    };

    const description =
        productTypeEn === ProductTypeEN.CREDIT
            ? translate('description.credit')
            : productTypeEn === ProductTypeEN.BALLON_LEASING ||
              productTypeEn === ProductTypeEN.OPERATIVE_LEASING ||
              productTypeEn === ProductTypeEN.VDL ||
              productTypeEn === ProductTypeEN.FINANCIAL_LEASE
            ? translate('description.balloon')
            : '';

    const checkboxLabel = (
        <p
            dangerouslySetInnerHTML={{
                __html: translate('agree-to-send'),
            }}
        />
    );

    const isLeasing = useMemo(
        () =>
            productTypeEn === ProductTypeEN.BALLON_LEASING ||
            productTypeEn === ProductTypeEN.OPERATIVE_LEASING ||
            productTypeEn === ProductTypeEN.VDL ||
            productTypeEn === ProductTypeEN.FINANCIAL_LEASE,
        [productTypeEn],
    );
    const isCredit = useMemo(() => productTypeEn === ProductTypeEN.CREDIT, [productTypeEn]);

    const isOriginalRegDocumentVisible = isLeasing;
    // isLeasing && !['CBEQ_FL', 'CBVO_FL', 'CBVO_FL', 'CBPN_FL', 'CBVE_FL'].includes(productType || ''); temporary revert CO-25354

    const isFlightReportUploadFieldRequired = useMemo(
        () => fieldNatureOfDamage.value === 'Vol',
        [fieldNatureOfDamage.value],
    );

    return (
        <>
            <Layout.Item>
                <ReparableSelection label={translate('reparable.label')} name={'vehicleClaim.reparable'} isMandatory />
            </Layout.Item>
            {fieldReparable.value === YesNoSelections.YES && (
                <Layout.Item>
                    <p>{translate('reparable.description')}</p>
                </Layout.Item>
            )}
            {fieldReparable.value === YesNoSelections.NO && (
                <>
                    <Layout.Item>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: description,
                            }}
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <EmailInput
                            label={translate('email')}
                            name={'vehicleClaim.nonReparableFields.email'}
                            isMandatory
                            aria-required={true}
                            inputId="email-vehicle-claim-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <PhonenumberInput
                            label={translate('phonenumber')}
                            name={'vehicleClaim.nonReparableFields.phonenumber'}
                            isMandatory
                            inputId="phone-number-vehicle-claim-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <NatureOfDamageSelection
                            label={translate('nature-of-damage.label')}
                            name={'vehicleClaim.nonReparableFields.natureOfDamage'}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <DateInput
                            className="c-form-field--no-floating"
                            label={translate('date-of-damage')}
                            aria-required={true}
                            name={'vehicleClaim.nonReparableFields.dateOfDamage'}
                            isMandatory={true}
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <CircumstancesOfDisasterSelection
                            label={translate('circumstances-of-the-disaster')}
                            name={'vehicleClaim.nonReparableFields.circumstancesOfTheDisaster'}
                            aria-required={true}
                            isMandatory
                            selectId="circumstances-of-the-disaster-vehicle-claim-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <CauseOfSinistreSelection
                            label={translate('cause-of-sinistre')}
                            name={'vehicleClaim.nonReparableFields.causeOfSinistre'}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <TextInput
                            label={translate('name-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.nameOfInsurer'}
                            aria-required={true}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <TextArea
                            label={translate('address-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.addressOfInsurer'}
                            aria-required={true}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <PostcodeInput
                            label={translate('postcode-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.postcodeOfInsurer'}
                            aria-required={true}
                            isMandatory
                            inputId="post-code-of-insurer-vehicle-claim-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <TextInput
                            label={translate('city-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.cityOfInsurer'}
                            aria-required={true}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <TextInput
                            label={translate('insurance-customer-number')}
                            name={'vehicleClaim.nonReparableFields.insuranceCustomerNumber'}
                            isMandatory={false}
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <TextInput
                            label={translate('damage-number')}
                            name={'vehicleClaim.nonReparableFields.damageNumber'}
                            isMandatory={false}
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <PhonenumberInput
                            label={translate('phonenumber-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.phonenumberOfInsurer'}
                            aria-required={true}
                            isMandatory
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <EmailInput
                            label={translate('email-of-insurer')}
                            name={'vehicleClaim.nonReparableFields.emailOfInsurer'}
                            aria-required={true}
                            isMandatory
                            inputId="email-of-insurer-vehicle-claim-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <ValidatedSelect
                            className="c-form-field--no-floating"
                            label={translate('expert-report-available')}
                            name={'vehicleClaim.nonReparableFields.expertReportAvailable'}
                            testId="expert-report-available-vehicle-claim-form"
                            selectItems={
                                [
                                    { label: YesNoSelections.YES, value: YesNoSelections.YES },
                                    { label: YesNoSelections.NO, value: YesNoSelections.NO },
                                ] as ValidatedSelectItem[]
                            }
                            emptyByDefault
                            aria-required={true}
                            isMandatory
                            required
                            requiredText=" *"
                        />
                    </Layout.Item>
                    {fieldExpertReportAvailable.value === YesNoSelections.NO && (
                        <Layout.Item>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: translate('expert-report-not-available-message'),
                                }}
                            />
                        </Layout.Item>
                    )}
                    {isOriginalRegDocumentVisible && (
                        <Layout.Item>
                            <OriginalRegDocumentSelection
                                label={translate('original-reg-document')}
                                name={'vehicleClaim.nonReparableFields.originalRegDocument'}
                                hasTooltip={isLeasing}
                                tooltipText={<div dangerouslySetInnerHTML={{ __html: translate('tooltip') }} />}
                                tooltipAriaLabel={translate('tooltipAriaLabel')}
                                tootltipTitle={translate('tooltipAriaLabel')}
                            />
                        </Layout.Item>
                    )}
                    {isOriginalRegDocumentVisible && selectedOriginalRegDocument === YesNoSelections.NO && (
                        <Layout.Item>
                            <FileUpload
                                label={translate('original-reg-document-info')}
                                name={'vehicleClaim.nonReparableFields.registrationFile'}
                                isMandatory={true}
                                multiple
                            />
                        </Layout.Item>
                    )}
                    {isOriginalRegDocumentVisible && selectedOriginalRegDocument === YesNoSelections.YES && (
                        <Layout.Item>
                            <ValidatedCheckbox
                                name={'vehicleClaim.nonReparableFields.agreeToSend'}
                                label={checkboxLabel}
                                isMandatory
                            />
                        </Layout.Item>
                    )}
                    <Layout.Item>{translate('files.label')}</Layout.Item>
                    {fieldExpertReportAvailable.value === YesNoSelections.YES && (
                        <Layout.Item>
                            <FileUpload
                                name={'vehicleClaim.nonReparableFields.files.expertReport'}
                                label={translate('files.expert-report')}
                                multiple
                            />
                        </Layout.Item>
                    )}
                    {isCredit && <CreditUpload productTypeEn={productTypeEn} bundledProducts={bundledProducts} />}
                    {isFlightReportUploadFieldRequired && (
                        <Layout.Item>
                            <FileUpload
                                name={'vehicleClaim.nonReparableFields.files.flightReport'}
                                label={translate('files.flight-report')}
                                isMandatory={true}
                                multiple
                            />
                        </Layout.Item>
                    )}
                    <Layout.Item>
                        <p>
                            <b>{translate('correct-information-provided-confirmation')}</b>
                        </p>
                    </Layout.Item>
                </>
            )}
        </>
    );
};
